import { Routes } from '@angular/router';

import {
  antiGuard,
  dialogCloseGuard,
  dzAdminGuard,
  hasAccessToPrintingsGuard,
  isAdminGuard,
  isAdminOrSuperAdminGuard,
  isLoggedGuard,
  isModelAdminGuard,
  isSuperAdminGuard,
} from './guards';

import {
  adminBannerResolver,
  adminDetailsResolver,
  currentAdminDataResolver,
  currentModelAdminResolver,
  superAdminResolver,
  userDetailsResolver,
} from './resolvers';

import { LoginComponent } from './login/login.component';
import { MainComponent } from './main/main.component';

export const appRoutes: Routes = [
  {
    path: '',
    component: MainComponent,
    canActivate: [isLoggedGuard],
    children: [
      {
        path: 'admin-management',
        loadComponent: () => import('./admin-management/admin-management.component').then(m => m.AdminManagementComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'admin-management/:page',
        loadComponent: () => import('./admin-management/admin-management.component').then(m => m.AdminManagementComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'user-management',
        loadComponent: () =>
          import('./global-users-management/global-users-management.component').then(m => m.GlobalUsersManagementComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'user-management/:page',
        loadComponent: () =>
          import('./global-users-management/global-users-management.component').then(m => m.GlobalUsersManagementComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'admin-details/:id',
        loadComponent: () => import('./admin-details/admin-details.component').then(m => m.AdminDetailsComponent),
        canActivate: [isSuperAdminGuard],
        runGuardsAndResolvers: 'always',
        resolve: {
          adminDetails: adminDetailsResolver,
        },
        children: [
          {
            path: 'admin-profile',
            loadComponent: () => import('./admin-profil-tab/admin-profil-tab.component').then(m => m.AdminProfilTabComponent),
          },
          {
            path: 'admin-users-list',
            loadComponent: () => import('./admin-users-list-tab/admin-users-list-tab.component').then(m => m.AdminUsersListTabComponent),
          },
          {
            path: 'admin-users-list/:page',
            loadComponent: () => import('./admin-users-list-tab/admin-users-list-tab.component').then(m => m.AdminUsersListTabComponent),
          },
          {
            path: 'banner',
            loadComponent: () => import('./banner-profil-tab/banner-profil-tab.component').then(m => m.BannerProfilTabComponent),
            runGuardsAndResolvers: 'always',
            resolve: {
              adminBanner: adminBannerResolver,
            },
          },
          {
            path: 'credit',
            loadComponent: () => import('./admin-credit/admin-credit.component').then(m => m.AdminCreditComponent),
            canActivate: [dzAdminGuard],
          },
        ],
      },
      {
        path: 'user-details/:id',
        loadComponent: () => import('./user-details/user-details.component').then(m => m.UserDetailsComponent),
        canActivate: [isAdminOrSuperAdminGuard],
        runGuardsAndResolvers: 'always',
        resolve: {
          userDetails: userDetailsResolver,
        },
        children: [
          {
            path: 'profile',
            loadComponent: () => import('./user-profile/user-profile.component').then(m => m.UserProfileComponent),
          },
          {
            path: 'credit',
            canActivate: [isAdminGuard],
            loadComponent: () => import('./profile-credit/profile-credit.component').then(m => m.ProfileCreditComponent),
            resolve: { adminDetails: currentAdminDataResolver },
          },
          {
            path: 'user-printing-log',
            loadComponent: () => import('./user-printing-log/user-printing-log.component').then(m => m.UserPrintingLogComponent),
          },
          {
            path: 'user-printing-log/:page',
            loadComponent: () => import('./user-printing-log/user-printing-log.component').then(m => m.UserPrintingLogComponent),
          },
        ],
      },
      {
        path: 'users-management',
        loadComponent: () => import('./admin-users-management/admin-users-management.component').then(m => m.AdminUsersManagementComponent),
        canActivate: [isAdminGuard],
        resolve: {
          currentAdminData: currentAdminDataResolver,
        },
      },
      {
        path: 'users-management/:page',
        loadComponent: () => import('./admin-users-management/admin-users-management.component').then(m => m.AdminUsersManagementComponent),
        canActivate: [isAdminGuard],
        resolve: {
          currentAdminData: currentAdminDataResolver,
        },
      },
      {
        path: 'super-admin-printing-log',
        loadComponent: () =>
          import('./super-admin-printing-log/super-admin-printing-log.component').then(m => m.SuperAdminPrintingLogComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'super-admin-credit-history',
        loadComponent: () => import('./super-admin-credit-log/super-admin-credit-log.component').then(m => m.SuperAdminCreditLogComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'super-admin-credit-history/:page',
        loadComponent: () => import('./super-admin-credit-log/super-admin-credit-log.component').then(m => m.SuperAdminCreditLogComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'super-admin-payment-history',
        loadComponent: () =>
          import('./super-admin-payment-document-log/super-admin-payment-document-log.component').then(
            m => m.SuperAdminPaymentDocumentLogComponent,
          ),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'super-admin-payment-history/:page',
        loadComponent: () =>
          import('./super-admin-payment-document-log/super-admin-payment-document-log.component').then(
            m => m.SuperAdminPaymentDocumentLogComponent,
          ),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'super-admin-printing-log/:page',
        loadComponent: () =>
          import('./super-admin-printing-log/super-admin-printing-log.component').then(m => m.SuperAdminPrintingLogComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'model-admin-management',
        loadComponent: () => import('./model-admin-management/model-admin-management.component').then(m => m.ModelAdminManagementComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'super-admin-profile',
        loadComponent: () => import('./super-admin-profile/super-admin-profile.component').then(m => m.SuperAdminProfileComponent),
        canActivate: [isSuperAdminGuard],
        resolve: {
          superAdminProfile: superAdminResolver,
        },
      },
      {
        path: 'admin-profile',
        loadComponent: () => import('./admin-profile/admin-profile.component').then(m => m.AdminProfileComponent),
        canActivate: [isAdminGuard],
        resolve: {
          currentAdminData: currentAdminDataResolver,
        },
      },
      {
        path: 'model-admin-profile',
        loadComponent: () => import('./model-admin-profile/model-admin-profile.component').then(m => m.ModelAdminProfileComponent),
        canActivate: [isModelAdminGuard],
        resolve: {
          currentModelAdmin: currentModelAdminResolver,
        },
      },
      {
        path: 'printable-data-management',
        loadComponent: () =>
          import('./printable-data-management/printable-data-management.component').then(m => m.PrintableDataManagementComponent),
        canActivate: [isModelAdminGuard],
      },
      {
        path: 'printable-data-management/:page',
        loadComponent: () =>
          import('./printable-data-management/printable-data-management.component').then(m => m.PrintableDataManagementComponent),
        canActivate: [isModelAdminGuard],
      },
      {
        path: 'management',
        loadComponent: () =>
          import('./brand-serie-model-management/brand-serie-model-management.component').then(m => m.BrandSerieModelManagementComponent),
        canActivate: [isModelAdminGuard],
        children: [
          {
            path: '',
            redirectTo: 'brand-management',
            pathMatch: 'full',
          },
          {
            path: 'brand-management',
            loadComponent: () => import('./brand-management/brand-management.component').then(m => m.BrandManagementComponent),
          },
          {
            path: 'brand-management/:page',
            loadComponent: () => import('./brand-management/brand-management.component').then(m => m.BrandManagementComponent),
          },
          {
            path: 'serie-management',
            loadComponent: () => import('./serie-management/serie-management.component').then(m => m.SerieManagementComponent),
          },
          {
            path: 'serie-management/:page',
            loadComponent: () => import('./serie-management/serie-management.component').then(m => m.SerieManagementComponent),
          },
          {
            path: 'model-management',
            loadComponent: () => import('./model-management/model-management.component').then(m => m.ModelManagementComponent),
          },
          {
            path: 'model-management/:page',
            loadComponent: () => import('./model-management/model-management.component').then(m => m.ModelManagementComponent),
          },
        ],
      },
      {
        path: 'admin-printing-log',
        loadComponent: () => import('./admin-printing-log/admin-printing-log.component').then(m => m.AdminPrintingLogComponent),
        canActivate: [isAdminGuard],
      },
      {
        path: 'admin-printing-log/:page',
        loadComponent: () => import('./admin-printing-log/admin-printing-log.component').then(m => m.AdminPrintingLogComponent),
        canActivate: [isAdminGuard],
      },
      {
        path: 'admin-credit-history',
        loadComponent: () => import('./admin-credit-log/admin-credit-log.component').then(m => m.AdminCreditLogComponent),
        canActivate: [isAdminGuard],
      },
      {
        path: 'admin-credit-history/:page',
        loadComponent: () => import('./admin-credit-log/admin-credit-log.component').then(m => m.AdminCreditLogComponent),
        canActivate: [isAdminGuard],
      },
      {
        path: 'admin-payment-document-history',
        loadComponent: () =>
          import('./admin-payment-document-log/admin-payment-document-log.component').then(m => m.AdminPaymentDocumentLogComponent),
        canActivate: [isAdminGuard],
      },
      {
        path: 'admin-payment-document-history/:page',
        loadComponent: () =>
          import('./admin-payment-document-log/admin-payment-document-log.component').then(m => m.AdminPaymentDocumentLogComponent),
        canActivate: [isAdminGuard],
      },
      {
        path: 'plotters',
        loadComponent: () => import('./plotter-log/plotter-log.component').then(m => m.PlotterLogComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'plotters/:page',
        loadComponent: () => import('./plotter-log/plotter-log.component').then(m => m.PlotterLogComponent),
        canActivate: [isSuperAdminGuard],
      },
      {
        path: 'error-404',
        loadComponent: () => import('./page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent),
        canActivate: [isLoggedGuard],
      },
    ],
  },
  { path: 'login', component: LoginComponent, canActivate: [antiGuard] },
  { path: 'login/:scope', component: LoginComponent, canActivate: [antiGuard] },
  {
    path: 'reset-password/:hash',
    loadComponent: () => import('./reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
    canActivate: [antiGuard],
  },
  {
    path: 'request-password-reset/:scope',
    loadComponent: () => import('./request-password-reset/request-password-reset.component').then(m => m.RequestPasswordResetComponent),
    canActivate: [antiGuard],
  },
  {
    path: 'success-payment',
    loadComponent: () =>
      import('./success-document-payment/success-document-payment.component').then(m => m.SuccessDocumentPaymentComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
  },
  {
    path: 'confirm-payment/:paymentDocumentId',
    loadComponent: () =>
      import('./stripe-payment-interface/stripe-payment-interface.component').then(m => m.StripePaymentInterfaceComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isAdminGuard],
  },
  {
    path: 'add-admin',
    loadComponent: () => import('./add-admin/add-admin.component').then(m => m.AddAdminComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isSuperAdminGuard],
  },
  {
    path: 'add-payment-document',
    loadComponent: () => import('./add-payment-document/add-payment-document.component').then(m => m.AddPaymentDocumentComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isSuperAdminGuard],
  },
  {
    path: 'add-user',
    loadComponent: () => import('./add-user/add-user.component').then(m => m.AddUserComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isAdminGuard],
  },
  {
    path: 'printing-visualisation/:id',
    loadComponent: () => import('./printing-visualisation/printing-visualisation.component').then(m => m.PrintingVisualisationComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isAdminOrSuperAdminGuard, hasAccessToPrintingsGuard],
  },
  {
    path: 'add-model-admin',
    loadComponent: () => import('./add-model-admin/add-model-admin.component').then(m => m.AddModelAdminComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isSuperAdminGuard],
  },
  {
    path: 'edit-model-admin/:id',
    loadComponent: () => import('./edit-model-admin/edit-model-admin.component').then(m => m.EditModelAdminComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isSuperAdminGuard],
  },
  {
    path: 'change-password',
    loadComponent: () => import('./change-password/change-password.component').then(m => m.ChangePasswordComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isLoggedGuard],
  },
  {
    path: 'edit-brand/:id',
    loadComponent: () => import('./edit-brand/edit-brand.component').then(m => m.EditBrandComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isModelAdminGuard],
  },
  {
    path: 'add-brand',
    loadComponent: () => import('./add-brand/add-brand.component').then(m => m.AddBrandComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isModelAdminGuard],
  },
  {
    path: 'add-serie',
    loadComponent: () => import('./add-serie/add-serie.component').then(m => m.AddSerieComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isModelAdminGuard],
  },
  {
    path: 'edit-serie/:id',
    loadComponent: () => import('./edit-serie/edit-serie.component').then(m => m.EditSerieComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isModelAdminGuard],
  },
  {
    path: 'add-model',
    loadComponent: () => import('./add-model/add-model.component').then(m => m.AddModelComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isModelAdminGuard],
  },
  {
    path: 'edit-model/:id',
    loadComponent: () => import('./edit-model/edit-model.component').then(m => m.EditModelComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isModelAdminGuard],
  },
  {
    path: 'add-printable-data',
    loadComponent: () => import('./add-printable-data/add-printable-data.component').then(m => m.AddPrintableDataComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isModelAdminGuard],
  },
  {
    path: 'printable-data-preview/:id',
    loadComponent: () => import('./printable-data-preview/printable-data-preview.component').then(m => m.PrintableDataPreviewComponent),
    outlet: 'dialog',
    canDeactivate: [dialogCloseGuard],
    canActivate: [isModelAdminGuard],
  },
  {
    path: '**',
    redirectTo: 'error-404',
  },
];
